/**
 * SpreadsheetToolbar.tsx - SpreadsheetToolbar - Toolbar for Spreadsheet
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useCallback } from 'react';
import { ExportMenu } from '@opcentral/outputtr';
import SpreadsheetColumnPicker from './SpreadsheetColumnPicker';
import './SpreadsheetToolbar.css';

export const SpreadsheetToolbar = ({
  gridRef,
  gridApi,
  columnApi,
  exportOptions = ['csv', 'json', 'xlsx'],
}) => {
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  
  // Clear all filters
  const handleClearFilters = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      gridApi.setQuickFilter('');
    }
  }, [gridApi]);
  
  // Refresh grid
  const handleRefresh = useCallback(() => {
    if (gridApi) {
      gridApi.refreshCells();
    }
  }, [gridApi]);
  
  // Auto-size all columns
  const handleAutoSize = useCallback(() => {
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    }
  }, [gridApi]);
  
  // Get data for export
  const getExportData = useCallback(() => {
    if (!gridApi) return [];
    
    const data = [];
    gridApi.forEachNode(node => {
      if (node.data) {
        data.push(node.data);
      }
    });
    
    return data;
  }, [gridApi]);
  
  // Handle search
  const handleSearch = useCallback((query) => {
    if (gridApi) {
      // AG Grid v34: Use setGridOption instead of setQuickFilter
      gridApi.setGridOption('quickFilterText', query);
    }
  }, [gridApi]);
  
  return (
    <div className="spreadsheet-toolbar">
      <div className="spreadsheet-toolbar-left">
        {/* Search */}
        <input
          type="text"
          className="spreadsheet-search-input"
          placeholder="Search all columns..."
          onChange={(e) => handleSearch(e.target.value)}
        />
        
        {/* Clear Filters */}
        <button
          className="spreadsheet-toolbar-button"
          onClick={handleClearFilters}
          title="Clear all filters"
        >
          🔄 Clear Filters
        </button>
      </div>
      
      <div className="spreadsheet-toolbar-right">
        {/* Auto-size columns */}
        <button
          className="spreadsheet-toolbar-button"
          onClick={handleAutoSize}
          title="Auto-size columns to fit"
        >
          ↔️ Fit
        </button>
        
        {/* Refresh */}
        <button
          className="spreadsheet-toolbar-button"
          onClick={handleRefresh}
          title="Refresh grid"
        >
          🔄 Refresh
        </button>
        
        {/* Column Picker */}
        <div className="spreadsheet-toolbar-dropdown">
          <button
            className="spreadsheet-toolbar-button"
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            title="Show/hide columns"
          >
            📋 Columns ▼
          </button>
          
          {showColumnPicker && (
            <SpreadsheetColumnPicker
              gridRef={gridRef}
              columnApi={columnApi}
              onClose={() => setShowColumnPicker(false)}
            />
          )}
        </div>
        
        {/* Export - Using shared ExportMenu with Parsely */}
        <div className="spreadsheet-toolbar-dropdown">
          <button
            className="spreadsheet-toolbar-button"
            onClick={() => setShowExportMenu(!showExportMenu)}
            title="Export data"
          >
            📤 Export ▼
          </button>
          
          {showExportMenu && (
            <ExportMenu
              getData={getExportData}
              formats={exportOptions}
              filename="spreadsheet-export"
              onClose={() => setShowExportMenu(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetToolbar;
